import { type MotionValue } from 'motion/react';
import { useEffect, useRef } from 'react';

import {
  BODY_PATH,
  LOGO_VIEW_BOX,
  WING_PATH,
  WING_PIVOT_X,
  WING_PIVOT_Y,
} from './logo-geometry';

type AppLogoProps = {
  size?: number | string;
  invert?: boolean;
  className?: string;
  customColor?: string;
  wingAngle?: MotionValue<number>;
  wingFold?: MotionValue<number>;
  eyeGaze?: MotionValue<number>;
  decorative?: boolean;
};

const WING_FOLD_ANGLE = -85;
const WING_FOLD_MIN_SCALE = 0.56;
const WING_SHADOW_OFFSET = 1.15;
const WING_FOLD_RADIANS = (WING_FOLD_ANGLE * Math.PI) / 180;
const WING_SHADOW_DX = WING_SHADOW_OFFSET * Math.sin(WING_FOLD_RADIANS);
const WING_SHADOW_DY = WING_SHADOW_OFFSET * Math.cos(WING_FOLD_RADIANS);
const EYE_CX = 17.3;
const EYE_CY = 6.55;
const EYE_PUPIL_RADIUS = 0.5;
const EYE_GAZE_SHIFT_X = 0.22;
const EYE_GAZE_SHIFT_Y = -0.3;

export const AppLogo = ({
  size = 24,
  invert = false,
  className,
  customColor,
  wingAngle,
  wingFold,
  eyeGaze,
  decorative = false,
}: AppLogoProps) => {
  const color =
    customColor || (invert ? 'var(--background)' : 'var(--foreground-default)');
  const wingGroupRef = useRef<SVGGElement>(null);
  const wingShadowRef = useRef<SVGPathElement>(null);
  const eyeCoverRef = useRef<SVGCircleElement>(null);
  const eyePupilRef = useRef<SVGCircleElement>(null);

  useEffect(() => {
    if (!eyeGaze) return;
    const applyGaze = (gaze: number) => {
      eyeCoverRef.current?.setAttribute('opacity', String(gaze));
      const pupil = eyePupilRef.current;
      if (pupil) {
        pupil.setAttribute('opacity', String(gaze));
        pupil.setAttribute('cx', String(EYE_CX + EYE_GAZE_SHIFT_X * gaze));
        pupil.setAttribute('cy', String(EYE_CY + EYE_GAZE_SHIFT_Y * gaze));
      }
    };
    applyGaze(eyeGaze.get());
    return eyeGaze.on('change', applyGaze);
  }, [eyeGaze]);

  useEffect(() => {
    if (!wingAngle && !wingFold) return;
    const applyWing = () => {
      const group = wingGroupRef.current;
      if (!group) return;
      const fold = wingFold?.get() ?? 0;
      const angle = (wingAngle?.get() ?? 0) + WING_FOLD_ANGLE * fold;
      const foldScale = 1 - (1 - WING_FOLD_MIN_SCALE) * fold;
      wingShadowRef.current?.setAttribute('opacity', String(fold));
      group.setAttribute(
        'transform',
        `rotate(${angle} ${WING_PIVOT_X} ${WING_PIVOT_Y}) translate(${WING_PIVOT_X} ${WING_PIVOT_Y}) scale(${foldScale}) translate(${-WING_PIVOT_X} ${-WING_PIVOT_Y})`
      );
    };
    applyWing();
    const unsubscribes = [
      wingAngle?.on('change', applyWing),
      wingFold?.on('change', applyWing),
    ];
    return () =>
      unsubscribes.forEach((unsubscribe) => unsubscribe && unsubscribe());
  }, [wingAngle, wingFold]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      viewBox={LOGO_VIEW_BOX}
      fill="none"
      overflow={wingAngle || wingFold ? 'visible' : undefined}
      className={className}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'Littlebird'}
      aria-hidden={decorative || undefined}
    >
      <path d={BODY_PATH} fill={color} />
      {eyeGaze && (
        <circle
          ref={eyeCoverRef}
          cx={EYE_CX}
          cy={EYE_CY}
          r={0.62}
          fill={color}
          opacity={0}
        />
      )}
      {eyeGaze && (
        <circle
          ref={eyePupilRef}
          cx={EYE_CX}
          cy={EYE_CY}
          r={EYE_PUPIL_RADIUS}
          fill="var(--background)"
          opacity={0}
        />
      )}
      <g ref={wingGroupRef}>
        <path
          ref={wingShadowRef}
          d={WING_PATH}
          transform={`translate(${WING_SHADOW_DX} ${WING_SHADOW_DY})`}
          fill="var(--background)"
          opacity={0}
        />
        <path d={WING_PATH} fill={color} />
      </g>
    </svg>
  );
};
